import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Repeat } from 'lucide-react';
import { formatMoney } from '@tcrd/shared';
import { Badge, Button, Card, EmptyState, ErrorState, Skeleton } from '@tcrd/ui';
import { Page } from '../../components/Page';
import { api, errorMessage } from '../../lib/api';
import { BILLING_LABEL, SERIES_STATUS, type RecurringSeries } from './types';

export function RecurringListPage({ base }: { base: '/app' | '/driver' }) {
  const q = useQuery({ queryKey: ['recurring'], queryFn: () => api<RecurringSeries[]>('/recurring-rides') });
  return (
    <Page
      title={base === '/app' ? 'Mis viajes recurrentes' : 'Viajes recurrentes'}
      back={base === '/app' ? '/app/home' : '/driver/more'}
      onRefresh={() => q.refetch()}
      actions={base === '/app' && <Link to="/app/recurring/new" aria-label="Nuevo viaje recurrente" className="tap flex items-center justify-center px-2"><Plus className="size-6" /></Link>}
    >
      {q.isLoading && <Skeleton className="h-28" />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {q.data?.length === 0 && (
        <EmptyState
          icon={<Repeat className="size-6" />}
          title="Sin viajes recurrentes"
          description={base === '/app' ? 'Ideal para ir al trabajo, la escuela, la universidad o citas médicas periódicas.' : 'Aquí verás las series de tus clientes.'}
          action={base === '/app' && <Link to="/app/recurring/new"><Button>Crear viaje recurrente</Button></Link>}
        />
      )}
      <div className="flex flex-col gap-3">
        {q.data?.map((s) => (
          <Link key={s.id} to={`${base}/recurring/${s.id}`}>
            <Card className="p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold truncate">{s.title}</p>
                <Badge tone={SERIES_STATUS[s.status][1]}>{SERIES_STATUS[s.status][0]}</Badge>
              </div>
              <p className="text-sm text-[var(--text-muted)]">{s.patternLabel} · ida {s.outboundTime}{s.returnTime ? ` · regreso ${s.returnTime}` : ''}</p>
              <p className="text-xs text-[var(--text-muted)] truncate mt-1">{s.origin.address} → {s.destination.address}</p>
              {base === '/driver' && <p className="text-xs mt-1">Cliente: {s.customer.name}</p>}
              {s.pricing && <p className="text-sm font-semibold mt-2">{BILLING_LABEL[s.pricing.billingMode]}: {formatMoney(s.pricing.amount)}</p>}
            </Card>
          </Link>
        ))}
      </div>
    </Page>
  );
}
